// components/CustomRatePlans.jsx
import React, { useEffect, useState } from "react";
import { Autocomplete, Checkbox, Chip, FormHelperText, TextField } from "@mui/material";

export default function CustomRatePlans({
  selectedValue = [],      // array<string>
  setSelectedValue,        // fn(array<string>)
  options = [],            // array<string>
  label,
  isFullWidth = false,
  handleSelectValue,       // unused (compat)
  labelId,
}) {
  const [allSelected, setAllSelected] = useState(false);
  const [displayOptions, setDisplayOptions] = useState([]);

  useEffect(() => {
    setAllSelected(options.length > 0 && selectedValue.length === options.length);
    setDisplayOptions(["Select All", ...options]);
  }, [options, selectedValue]);

  const handleChange = (event, newValue, reason, details) => {
    const clicked = details?.option;

    if (reason === "clear") {
      setSelectedValue([]);
      return;
    }

    if (clicked === "Select All") {
      // Toggle all on/off
      setSelectedValue(allSelected ? [] : options);
      return;
    }

    // Normal toggle; strip "Select All" if present in newValue
    setSelectedValue((newValue || []).filter((v) => v !== "Select All"));
  };

  const clearAll = (e) => {
    // Prevent MUI's default tag-remove handler from also firing
    e?.stopPropagation?.();
    setSelectedValue([]);
  };

  return (
    <>
      <Autocomplete
        sx={{ mt: "10px" }}
        multiple
        disableCloseOnSelect

        /* 👇 Add these */
        disablePortal
        slotProps={{
          popper: {
            placement: "bottom-start",
            modifiers: [{ name: "flip", enabled: false }], // don't flip above
            sx: { zIndex: (theme) => theme.zIndex.modal + 1 }, // stay on top of cards/dialogs
          },
          paper: {
            sx: { maxHeight: 6 * 38 + 8, overflow: "auto" }, // similar to your MenuProps
          },
        }}
        /* If your MUI version doesn't support slotProps, use componentsProps instead:
        componentsProps={{
          popper: { placement: "bottom-start", modifiers: [{ name: "flip", enabled: false }], sx: { zIndex: (theme) => theme.zIndex.modal + 1 } },
          paper:  { sx: { maxHeight: 6 * 38 + 8, overflow: "auto" } },
        }}
        */

        options={displayOptions}
        value={selectedValue}
        onChange={handleChange}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        limitTags={-1}
        renderTags={(value, getTagProps) => {
          if (options.length > 0 && value.length === options.length) {
            return [
              <Chip
                key="__select_all__"
                label={`Select All (${options.length})`}
                onDelete={(e) => {
                  e.stopPropagation();
                  setSelectedValue([]);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />,
            ];
          }
          return value.map((opt, index) => (
            <Chip key={opt} label={opt} {...getTagProps({ index })} />
          ));
        }}
        renderOption={(props, option) => {
          const checked = option === "Select All" ? allSelected : selectedValue.includes(option);
          return (
            <li {...props}>
              <Checkbox sx={{ mr: 1 }} checked={checked} />
              {option}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            id={labelId}
            label={label || undefined}
            placeholder={label ? undefined : "Search..."}
            fullWidth={isFullWidth}
          />
        )}
      />

      <FormHelperText sx={{ mt: 0.5 }}>
        {selectedValue.length > 0 ? `${selectedValue.length} selected` : ""}
      </FormHelperText>
    </>
  );
}
